/**
 * Archivio chiave/valore JSON durevole per l'uso offline.
 * - App nativa: file in Directory.Data (sopravvive a riavvii e a pulizie della cache WebView).
 * - Web: localStorage.
 * Le scritture sono serializzate per chiave (niente corse tra due salvataggi ravvicinati).
 */
import { Capacitor } from "@capacitor/core";
import { Directory, Encoding, Filesystem } from "@capacitor/filesystem";

const DIR = "ml-offline";
const WEB_PREFIX = "ml:offline:";

const writeChains = new Map<string, Promise<unknown>>();

function safeName(key: string): string {
  return key.replace(/[^a-zA-Z0-9._-]/g, "_");
}

function native(): boolean {
  return Capacitor.isNativePlatform();
}

export async function kvGet<T>(key: string): Promise<T | null> {
  try {
    if (native()) {
      const res = await Filesystem.readFile({
        path: `${DIR}/${safeName(key)}.json`,
        directory: Directory.Data,
        encoding: Encoding.UTF8,
      });
      const text = typeof res.data === "string" ? res.data : await (res.data as Blob).text();
      return JSON.parse(text) as T;
    }
    const raw = localStorage.getItem(WEB_PREFIX + key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null; // file assente o JSON corrotto → come "nessun dato"
  }
}

export function kvSet<T>(key: string, value: T): Promise<void> {
  const prev = writeChains.get(key) ?? Promise.resolve();
  const next = prev
    .catch(() => undefined)
    .then(async () => {
      const text = JSON.stringify(value);
      if (native()) {
        await Filesystem.writeFile({
          path: `${DIR}/${safeName(key)}.json`,
          directory: Directory.Data,
          encoding: Encoding.UTF8,
          data: text,
          recursive: true,
        });
      } else {
        localStorage.setItem(WEB_PREFIX + key, text);
      }
    });
  writeChains.set(key, next);
  return next as Promise<void>;
}

export async function kvRemove(key: string): Promise<void> {
  const prev = writeChains.get(key) ?? Promise.resolve();
  await prev.catch(() => undefined);
  try {
    if (native()) {
      await Filesystem.deleteFile({ path: `${DIR}/${safeName(key)}.json`, directory: Directory.Data });
    } else {
      localStorage.removeItem(WEB_PREFIX + key);
    }
  } catch {
    /* già assente */
  }
}
